import { CaretUp, CaretDown, Minus } from "@phosphor-icons/react";

// EnvironmentalStatus — reusable metric card with severity, freshness and source.
// Every environmental data point in NaturePulse should be surfaced through this
// component so users see the SAME chrome (value, unit, status label, trend,
// source, freshness) whether it's AQI, UV, temperature or noise.

export const SEVERITY = {
  low:      { label: "Low",      dot: "#16a34a", bg: "rgba(22,163,74,0.08)",  text: "#166534" },
  good:     { label: "Good",     dot: "#16a34a", bg: "rgba(22,163,74,0.08)",  text: "#166534" },
  moderate: { label: "Moderate", dot: "#eab308", bg: "rgba(234,179,8,0.10)",  text: "#854d0e" },
  poor:     { label: "Poor",     dot: "#f97316", bg: "rgba(249,115,22,0.12)", text: "#9a3412" },
  high:     { label: "High",     dot: "#ef4444", bg: "rgba(239,68,68,0.10)",  text: "#991b1b" },
  severe:   { label: "Severe",   dot: "#7f1d1d", bg: "rgba(127,29,29,0.12)",  text: "#7f1d1d" },
  unknown:  { label: "Unknown",  dot: "#94a3b8", bg: "rgba(148,163,184,0.10)", text: "#334155" },
};

export function aqiToSeverity(aqi) {
  if (aqi == null || isNaN(aqi)) return "unknown";
  if (aqi <= 50) return "good";
  if (aqi <= 100) return "moderate";
  if (aqi <= 150) return "poor";
  if (aqi <= 200) return "high";
  return "severe";
}

export function uvToSeverity(uv) {
  if (uv == null || isNaN(uv)) return "unknown";
  if (uv < 3) return "low";
  if (uv < 6) return "moderate";
  if (uv < 8) return "poor";
  if (uv < 11) return "high";
  return "severe";
}

function DataFreshness({ updatedAt }) {
  if (!updatedAt) return null;
  const ts = new Date(updatedAt);
  if (isNaN(ts.getTime())) return null;
  const minutes = Math.max(0, Math.round((Date.now() - ts.getTime()) / 60000));
  let label; let live = false;
  if (minutes < 5) { label = "Live"; live = true; }
  else if (minutes < 30) label = `Updated ${minutes} min ago`;
  else if (minutes < 120) label = `Updated ${minutes} min ago`;
  else if (minutes < 60 * 24) label = `Updated ${Math.round(minutes / 60)} hr ago`;
  else label = `Delayed · ${Math.round(minutes / 60 / 24)} d`;
  return (
    <span
      data-testid="data-freshness"
      className={`inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase ${live ? "text-emerald-600" : "text-[color:var(--np-muted,#3A3830)]"}`}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${live ? "bg-emerald-500 animate-pulse" : "bg-current opacity-50"}`} />
      {label}
    </span>
  );
}

export default function EnvironmentalStatus({
  label,
  value,
  unit = "",
  severity = "unknown",
  trend,           // "up" | "down" | "flat" | number (delta)
  trendLabel,      // "↑ 12% vs yesterday"
  source,
  updatedAt,
  hint,            // one-line human explanation
  icon,            // optional <Icon /> node
  compact = false,
  onClick,
  testid,
}) {
  const sev = SEVERITY[severity] || SEVERITY.unknown;
  const Trend = trend === "up" ? CaretUp : trend === "down" ? CaretDown : Minus;
  const displayValue = value == null || value === "" || (typeof value === "number" && isNaN(value))
    ? "—" : value;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid || `envstatus-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}
      className={`group text-left w-full ${onClick ? "cursor-pointer" : "cursor-default"} border border-black/10 bg-white/60 backdrop-blur rounded-xl overflow-hidden hover:border-black/25 transition-colors ${compact ? "p-3" : "p-4"}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {icon && <span className="text-[color:var(--np-brand,#B85F2A)] shrink-0">{icon}</span>}
          <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-text-2,#3A3830)] truncate">
            {label}
          </span>
        </div>
        <span
          className="shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-mono-tech tracking-[0.18em] uppercase"
          style={{ background: sev.bg, color: sev.text }}
          aria-label={`Status: ${sev.label}`}
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: sev.dot }} />
          {sev.label}
        </span>
      </div>

      <div className="mt-2 flex items-baseline gap-1.5">
        <div className={`font-serif-display leading-none ${compact ? "text-3xl" : "text-4xl md:text-5xl"} text-[color:var(--np-text,#1B4332)]`}>
          {displayValue}
        </div>
        {unit && (
          <span className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)]">
            {unit}
          </span>
        )}
      </div>

      {(hint || trendLabel) && (
        <div className="mt-2 flex items-center gap-2 text-[12px] text-[color:var(--np-text-2,#3A3830)]">
          {trendLabel && (
            <span className="inline-flex items-center gap-0.5 font-mono-tech text-[10px] tracking-[0.18em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              <Trend size={10} weight="bold" />{trendLabel}
            </span>
          )}
          {hint && <span className="truncate">{hint}</span>}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2">
        <DataFreshness updatedAt={updatedAt} />
        {source && (
          <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)] truncate">
            {source}
          </span>
        )}
      </div>
    </button>
  );
}
